/**
 * Credits (§7.2, §8.4).
 *
 * Expiry warnings use `creditExpiryCopy` verbatim: credits are prepaid and
 * non-refundable after expiry, so the warning is a commitment about what happens
 * to money already taken, not a marketing nudge.
 *
 * A credit pack is a platform charge, so ordinary merchant language is correct
 * here — the platform genuinely is the merchant for it (§9.1).
 */

import { useState } from 'react';
import { creditExpiryCopy } from '@pm/shared';
import { normalizeCreditPack } from '../../api/client';
import { useApi, useMutation } from '../../hooks/useApi';
import type { CreditsSummary } from '../../api/types';
import { Card, Stat, Loading, Banner } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { EmptyState } from '../../components/EmptyState';
import { Dialog } from '../../components/Dialog';
import { amountLabel, localDate } from '../../lib/format';
import { routes } from '../../api/routes';

export function Credits() {
  const credits = useApi<CreditsSummary>(routes.me.credits);
  const [chosen, setChosen] = useState<string | null>(null);
  const purchase = useMutation<Record<string, unknown>, unknown>(routes.me.creditPurchase, {
    idempotent: true,
  });

  if (credits.initialLoading) return <Loading label="Loading credits" />;
  if (credits.problem) return <ErrorView problem={credits.problem} onRetry={credits.reload} />;
  if (!credits.data) return null;

  const c = credits.data;
  // Packs come straight off the active fee schedule, whose stored key for the
  // amount is not the one this client uses. `normalizeCreditPack` is the single
  // place that reconciles the two.
  const packs = (c.packs ?? []).map((p) => normalizeCreditPack(p as unknown as Record<string, unknown>));
  const pack = packs.find((p) => p.sku === chosen) ?? null;

  return (
    <div className="credits">
      {c.expiring.map((e) => (
        <Banner key={e.expires_at} tone="warn" title="Credits expiring">
          {creditExpiryCopy(e.count, localDate(e.expires_at))}
        </Banner>
      ))}

      <Card title="Balance">
        <dl className="stats">
          <Stat label="Text credits" value={c.text_credits} />
          <Stat label="Image credits" value={c.image_credits} />
        </dl>
      </Card>

      <Card title="Purchase more">
        {packs.length === 0 ? (
          <EmptyState heading="No packs available" body="Nothing is on sale right now." />
        ) : (
          <ul className="pack-grid">
            {packs.map((p) => (
              <li key={p.sku} className="pack">
                <h3>{p.label}</h3>
                <p className="pack-amount">{amountLabel(p.amount)}</p>
                <p className="muted">
                  {p.text_credits} text · {p.image_credits} image
                </p>
                <button type="button" className="btn btn-primary" onClick={() => setChosen(p.sku)}>
                  Purchase
                </button>
              </li>
            ))}
          </ul>
        )}
        {purchase.problem ? <InlineError problem={purchase.problem} /> : null}
      </Card>

      <Card title="Lots and expiry">
        {c.lots.length === 0 ? (
          <EmptyState heading="No credit lots" body="Purchased credits appear here with their expiry date." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Pack</th>
                <th scope="col">Text left</th>
                <th scope="col">Image left</th>
                <th scope="col">Purchased</th>
                <th scope="col">Expires</th>
              </tr>
            </thead>
            <tbody>
              {c.lots.map((lot) => (
                <tr key={lot.id}>
                  <td>{lot.sku}</td>
                  <td>{lot.text_remaining}</td>
                  <td>{lot.image_remaining}</td>
                  <td>{localDate(lot.purchased_at)}</td>
                  <td>{localDate(lot.expires_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog
        open={pack !== null}
        onClose={() => setChosen(null)}
        title="Confirm this purchase"
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setChosen(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={async () => {
                if (!pack) return;
                setChosen(null);
                if ((await purchase.run({ sku: pack.sku })) !== null) {
                  purchase.reset();
                  credits.reload();
                }
              }}
            >
              Charge my card
            </button>
          </>
        }
      >
        {pack ? (
          <>
            <p>
              {pack.label} — {amountLabel(pack.amount)} for {pack.text_credits} text and{' '}
              {pack.image_credits} image credits.
            </p>
            <p className="muted">
              Credits expire; after expiry they cannot be refunded or transferred.
            </p>
          </>
        ) : null}
      </Dialog>
    </div>
  );
}
